import { Link } from 'react-router-dom';

export function Privacy() {
  return (
    <div
      className="min-h-screen h-screen overflow-y-auto"
      style={{ backgroundColor: 'var(--color-bg-primary)' }}
    >
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <Link
            to="/"
            className="text-sm hover:underline"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            &larr; Home
          </Link>
          <Link
            to="/privacy/ko"
            className="text-sm hover:underline"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Korean
          </Link>
        </div>

        <h1
          className="text-3xl font-bold mb-2"
          style={{ color: 'var(--color-text-primary)' }}
        >
          Privacy Policy
        </h1>
        <p
          className="text-sm mb-8"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          Last updated: December 7, 2025
        </p>

        <div
          className="space-y-8 text-sm leading-relaxed"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {/* Introduction */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Introduction
            </h2>
            <p>
              Pelo1, operated by{' '}
              <a
                href="https://baselabs.co.kr"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                BaseLabs Corp.
              </a>{' '}
              (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;), is a cycling
              telemetry visualization service that displays real-time cycling
              data in an F1-style dashboard. This Privacy Policy explains how we
              collect, use, store, and protect your personal information when
              you use our service.
            </p>
          </section>

          {/* Data Controller */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Data Controller
            </h2>
            <p className="mb-3">
              The data controller responsible for your personal data is:
            </p>
            <div className="pl-4 border-l-2" style={{ borderColor: 'var(--color-border)' }}>
              <p>
                <strong style={{ color: 'var(--color-text-primary)' }}>
                  BaseLabs Corp. (베이스랩스 주식회사)
                </strong>
              </p>
              <p>서울시 송파구 중대로 135 서관 11층</p>
              <p>11F West Building, 135 Jungdae-ro, Songpa-gu, Seoul, Republic of Korea</p>
              <p>
                Email:{' '}
                <a
                  href="mailto:privacy-pelo1@baselabs.co.kr"
                  className="hover:underline"
                  style={{ color: 'var(--color-accent-red)' }}
                >
                  privacy-pelo1@baselabs.co.kr
                </a>
              </p>
            </div>
          </section>

          {/* Legal Basis for Processing */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Legal Basis for Processing
            </h2>
            <p className="mb-3">
              We process your personal data based on the following legal grounds
              under GDPR and applicable data protection laws:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong>Consent:</strong> When you create an account, connect
                third-party services (Strava, Garmin), or upload activity files,
                you provide explicit consent for us to process your data.
              </li>
              <li>
                <strong>Contract Performance:</strong> Processing necessary to
                provide you with our service, including displaying your cycling
                data and creating replays.
              </li>
              <li>
                <strong>Legitimate Interests:</strong> For service improvement,
                security monitoring, and fraud prevention, where such interests
                are not overridden by your rights.
              </li>
            </ul>
          </section>

          {/* Data We Collect */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Data We Collect
            </h2>
            <p className="mb-3">
              When you use Pelo1, we collect the following types of data:
            </p>

            <h3
              className="text-base font-medium mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Activity Data (from Garmin devices)
            </h3>
            <ul className="list-disc list-inside mb-4 space-y-1">
              <li>GPS location (latitude and longitude)</li>
              <li>Speed</li>
              <li>Heart rate</li>
              <li>Power output (watts)</li>
              <li>Cadence</li>
              <li>Altitude/Elevation</li>
              <li>Distance traveled</li>
              <li>Elapsed time</li>
              <li>Device battery level</li>
            </ul>

            <h3
              className="text-base font-medium mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              FIT File Data
            </h3>
            <p className="mb-4">
              When you upload Garmin FIT files for replay functionality, we
              process activity records including GPS coordinates, timestamps,
              and performance metrics contained within those files.
            </p>

            <h3
              className="text-base font-medium mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Account Information
            </h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Email address</li>
              <li>Nickname/Display name</li>
              <li>Profile image (if using social login)</li>
            </ul>
          </section>

          {/* How We Use Your Data */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              How We Use Your Data
            </h2>
            <p className="mb-3">We use your data for the following purposes:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Real-time Visualization:</strong> Display your cycling
                data on a live dashboard during group rides
              </li>
              <li>
                <strong>Event Replay:</strong> Enable playback of completed
                events so you can review your ride
              </li>
              <li>
                <strong>Virtual Group Rides:</strong> Compare your FIT file data
                with other riders in a simulated group ride
              </li>
              <li>
                <strong>Performance Analysis:</strong> Show charts and
                statistics of your cycling performance
              </li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Data Sharing
            </h2>
            <p className="mb-3">Your activity data may be shared with:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Event Participants:</strong> Other riders in the same
                event can see your real-time location and telemetry
              </li>
              <li>
                <strong>Event Spectators:</strong> Anyone with the event link
                can view the live dashboard, including your position and
                performance data
              </li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> sell your personal data to third
              parties. We do <strong>not</strong> share your data with
              advertisers or marketing companies.
            </p>
          </section>

          {/* Third-Party Service Providers */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Third-Party Service Providers
            </h2>
            <p className="mb-3">
              We use the following third-party services to operate Pelo1. These
              providers may process your data as described:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong>Amazon Web Services (AWS):</strong> Cloud infrastructure
                for hosting our servers and storing data (servers located in
                Asia Pacific - Seoul region)
              </li>
              <li>
                <strong>Mapbox:</strong> Map visualization services for
                displaying cycling routes and positions
              </li>
              <li>
                <strong>Google:</strong> OAuth authentication for social login
              </li>
              <li>
                <strong>Cloudflare:</strong> DNS and security services
              </li>
              <li>
                <strong>Sentry:</strong> Error monitoring and performance
                tracking (anonymized data only)
              </li>
            </ul>
            <p className="mt-3">
              Each of these providers has their own privacy policy governing
              their use of data.
            </p>
          </section>

          {/* Data Storage and Retention */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Data Storage and Retention
            </h2>
            <p className="mb-3">
              We retain your data for the following periods:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong>Account Information:</strong> Retained until you delete
                your account
              </li>
              <li>
                <strong>Activity Telemetry &amp; Files:</strong> Retained
                indefinitely to enable replay functionality. When you delete
                your account, your telemetry data and uploaded files (FIT, GPX)
                are automatically deleted.
              </li>
              <li>
                <strong>Server Logs:</strong> Retained for 30 days for security
                and debugging purposes
              </li>
            </ul>

            <h3
              className="text-base font-medium mb-2 mt-4"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Group Ride Data Handling (Data Preservation Policy)
            </h3>
            <p className="mb-3">
              When you participate in group rides with other users, the
              following rules apply upon account deletion. We prioritize data
              preservation while removing your personal identity:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong>Solo events you created:</strong> Your account
                information is removed, but the event data (including telemetry
                and files) is preserved and attributed to a &quot;Deleted
                User&quot; account for service integrity.
              </li>
              <li>
                <strong>Group events you created:</strong> Your riding data is
                retained but anonymized (attributed to &quot;Deleted User&quot;).
                The event is preserved for other participants, with ownership
                transferred to the &quot;Deleted User&quot; account.
              </li>
              <li>
                <strong>Events you joined:</strong> Your riding data is retained
                but attributed to &quot;Deleted User&quot;. Other
                participants&apos; data remains unaffected.
              </li>
              <li>
                <strong>Likes and chat messages:</strong> Your likes are
                preserved (anonymized). Chat messages are preserved but the
                author is shown as &quot;Deleted User.&quot;
              </li>
            </ul>
            <p className="mt-3">
              This approach preserves the integrity of group ride data and event
              statistics while removing your personal identity from the system.
              Your authentication credentials (login tokens, connected social
              accounts) are permanently deleted.
            </p>
          </section>

          {/* International Data Transfers */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              International Data Transfers
            </h2>
            <p>
              Your data is primarily stored on servers located in South Korea
              (AWS Asia Pacific - Seoul). However, some of our third-party
              service providers may process data in other countries. When data
              is transferred outside of your country, we ensure appropriate
              safeguards are in place, including standard contractual clauses
              approved by relevant authorities.
            </p>
          </section>

          {/* Cookies and Tracking */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Cookies and Tracking
            </h2>
            <p className="mb-3">Pelo1 uses minimal cookies and local storage:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong>Essential Cookies:</strong> Authentication tokens to
                keep you logged in (stored in localStorage)
              </li>
              <li>
                <strong>Preferences:</strong> Language settings and UI
                preferences (stored in localStorage)
              </li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> use advertising cookies or third-party
              tracking pixels. We do <strong>not</strong> use analytics services
              that track individual users across websites.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Data Security
            </h2>
            <p className="mb-3">
              We implement appropriate technical and organizational measures to
              protect your personal data:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>All data transmission is encrypted using HTTPS/TLS</li>
              <li>Passwords are hashed using industry-standard algorithms</li>
              <li>Database access is restricted and monitored</li>
              <li>Regular security updates and vulnerability assessments</li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Children&apos;s Privacy
            </h2>
            <p>
              Pelo1 is not intended for children under the age of 16. We do not
              knowingly collect personal data from children under 16. If you are
              a parent or guardian and believe your child has provided us with
              personal data, please contact us and we will delete such
              information.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Your Rights
            </h2>
            <p className="mb-3">
              Under GDPR and applicable data protection laws, you have the
              following rights:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong>Access:</strong> Request a copy of the personal data we
                hold about you
              </li>
              <li>
                <strong>Rectification:</strong> Request correction of inaccurate
                or incomplete data
              </li>
              <li>
                <strong>Erasure:</strong> Request deletion of your personal data
                (&quot;right to be forgotten&quot;)
              </li>
              <li>
                <strong>Restriction:</strong> Request restriction of processing
                in certain circumstances
              </li>
              <li>
                <strong>Portability:</strong> Request your data in a structured,
                machine-readable format
              </li>
              <li>
                <strong>Objection:</strong> Object to processing based on
                legitimate interests
              </li>
              <li>
                <strong>Withdraw Consent:</strong> Withdraw your consent at any
                time where processing is based on consent
              </li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, please contact us at{' '}
              <a
                href="mailto:privacy-pelo1@baselabs.co.kr"
                className="hover:underline"
                style={{ color: 'var(--color-accent-red)' }}
              >
                privacy-pelo1@baselabs.co.kr
              </a>
              . We will respond within 30 days.
            </p>
            <p className="mt-3">
              If you are in the European Union and believe we have not
              adequately addressed your concerns, you have the right to lodge a
              complaint with your local data protection supervisory authority.
            </p>
          </section>

          {/* Garmin Connect Integration */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Garmin Connect Integration
            </h2>
            <p>
              Pelo1 integrates with Garmin Connect to access your activity data.
              When you authorize this connection, we receive access to your
              activity data as permitted by Garmin&apos;s API. You can revoke
              this access at any time through your Garmin Connect account
              settings.
            </p>
          </section>

          {/* Strava Integration */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Strava Integration
            </h2>
            <p className="mb-3">
              Pelo1 integrates with Strava to import your cycling activities for
              replay visualization. When you connect your Strava account, you
              authorize us to access your data as described below.
            </p>

            <h3
              className="text-base font-medium mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Data We Collect from Strava
            </h3>
            <ul className="list-disc list-inside mb-4 space-y-1">
              <li>
                Activity streams (GPS coordinates, heart rate, power, cadence,
                altitude)
              </li>
              <li>Activity metadata (name, date, distance, duration)</li>
              <li>Athlete profile information (FTP)</li>
            </ul>

            <h3
              className="text-base font-medium mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              How We Use Strava Data
            </h3>
            <ul className="list-disc list-inside mb-4 space-y-1">
              <li>Create replay visualizations of your cycling activities</li>
              <li>Display power training zones based on your FTP</li>
              <li>Enable virtual group rides by combining multiple activities</li>
            </ul>

            <h3
              className="text-base font-medium mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Strava Data Sharing
            </h3>
            <p className="mb-4">
              When you create a replay from your Strava activity, that replay
              may be viewable by other users. This allows for group ride
              comparisons and shared viewing experiences. We do not sell or
              share your Strava data with third parties for advertising or
              marketing purposes.
            </p>

            <h3
              className="text-base font-medium mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Disconnecting Strava
            </h3>
            <p className="mb-4">
              You can disconnect your Strava account at any time from your
              Profile settings. Upon disconnection, your Strava access token
              will be revoked. Previously imported activity data will be
              retained to enable replay functionality. To request deletion of
              your Strava-sourced data, please contact us at{' '}
              <a
                href="mailto:privacy-pelo1@baselabs.co.kr"
                className="hover:underline"
                style={{ color: 'var(--color-accent-red)' }}
              >
                privacy-pelo1@baselabs.co.kr
              </a>
              .
            </p>

            <h3
              className="text-base font-medium mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Strava&apos;s Data Collection
            </h3>
            <p>
              Please note that Strava may collect and use data related to your
              access of Pelo1&apos;s Strava integration for their own purposes,
              as described in{' '}
              <a
                href="https://www.strava.com/legal/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                style={{ color: 'var(--color-accent-red)' }}
              >
                Strava&apos;s Privacy Policy
              </a>
              .
            </p>
          </section>

          {/* Changes to This Policy */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will
              notify you of any significant changes by posting the new policy on
              this page with an updated revision date. For material changes, we
              may also notify you via email if you have an account.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2
              className="text-xl font-semibold mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Contact Us
            </h2>
            <p>
              If you have any questions about this Privacy Policy or wish to
              exercise your data rights, please contact us at:
            </p>
            <div
              className="mt-3 pl-4 border-l-2"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <p>
                <strong style={{ color: 'var(--color-text-primary)' }}>
                  BaseLabs Corp.
                </strong>
              </p>
              <p>
                Email:{' '}
                <a
                  href="mailto:privacy-pelo1@baselabs.co.kr"
                  className="hover:underline"
                  style={{ color: 'var(--color-accent-red)' }}
                >
                  privacy-pelo1@baselabs.co.kr
                </a>
              </p>
              <p>
                General Inquiries:{' '}
                <a
                  href="mailto:contact-pelo1@baselabs.co.kr"
                  className="hover:underline"
                  style={{ color: 'var(--color-accent-red)' }}
                >
                  contact-pelo1@baselabs.co.kr
                </a>
              </p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div
          className="mt-12 pt-8 border-t text-center text-sm"
          style={{
            borderColor: 'var(--color-border)',
            color: 'var(--color-text-muted)',
          }}
        >
          <div className="flex justify-center gap-4 mb-2">
            <Link
              to="/terms"
              className="hover:underline"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Terms of Service
            </Link>
            <span style={{ color: 'var(--color-border)' }}>|</span>
            <Link
              to="/privacy"
              className="hover:underline"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Privacy Policy
            </Link>
            <span style={{ color: 'var(--color-border)' }}>|</span>
            <Link
              to="/contact"
              className="hover:underline"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Contact
            </Link>
          </div>
          &copy; 2025{' '}
          <a
            href="https://baselabs.co.kr"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            BaseLabs Corp.
          </a>{' '}
          All rights reserved.
        </div>
      </div>
    </div>
  );
}
